import { useState } from 'react';
import useMedia from 'use-media';
import get_ from 'lodash/get';
import { format, parseISO } from 'date-fns';
import { useLazyQuery, useQuery } from '@yema/commerce/APIClient';
import { trackError } from '@yema/commerce/utils/error';
import { useSelector } from '@yema/commerce/StoreClient';
import { useStateEngine } from '@yema/commerce';
import { APP_STATE_TYPES, MODAL_KIND } from '@yema/commerce/CommerceProvider';
import InvoiceRequest from '@/yema-designkit/webkit/organisms/InvoiceRequest';
import QUERY_T3B_TICKET from '@yema/commerce/graphql/queries/invoice/t3b-tickets.gql';
import ORDER_FOR_INVOICE from '@yema/commerce/graphql/queries/invoice/order-for-invoice.gql';
import ME_BASIC_QUERY from '@yema/commerce/graphql/queries/user/me-basic.gql';

const INITIAL_TICKET_STATE = {
    isInitialState: true,
    ticketId: '',
    ticketFound: false,
    ticketTotalSale: '',
    message: '',
    canRequest: null,
};

const INITIAL_TICKET_VALUES = {
    posId: '',
    storeId: '',
    ticketId: '',
    ticketDate: '',
};

const INITIAL_ORDER_STATE = {
    isInitialState: true,
    total: '',
    date: '',
    found: false,
    message: '',
    canRequest: null,
    publicId: '',
};

const INITIAL_ORDER_VALUES = {
    publicId: '',
    total: '',
};

const InvoicePage = () => {
    const isMobile = useMedia({ maxWidth: 640 });
    const userIsLogged = useSelector(({ authentication }) => authentication.token !== null);

    const [_, dispatch] = useStateEngine();

    const [ticketResult, setTicketResult] = useState(INITIAL_TICKET_STATE);
    const [orderResult, setOrderResult] = useState(INITIAL_ORDER_STATE);
    const [orderValues, setOrderValues] = useState(INITIAL_ORDER_VALUES);
    const [ticketValues, setTicketValues] = useState(INITIAL_TICKET_VALUES);

    const { data: dataMeBasic } = useQuery(ME_BASIC_QUERY, {
        ssr: false,
        skip: !userIsLogged,
        fetchPolicy: 'cache-first',
    });

    const [getTicket, { loading: loadingTicket, error: ticketError }] = useLazyQuery(
        QUERY_T3B_TICKET,
        {
            fetchPolicy: 'network-only',
        }
    );
    const [getOrder, { loading: loadingOrder, error: orderError }] = useLazyQuery(
        ORDER_FOR_INVOICE,
        {
            fetchPolicy: 'network-only',
        }
    );

    const handleResetValues = (type) => {
        switch (type) {
            case 'ticket':
                setTicketValues(INITIAL_TICKET_VALUES);
                setTicketResult(INITIAL_TICKET_STATE);
                break;
            case 'order':
                setOrderValues(INITIAL_ORDER_VALUES);
                setOrderResult(INITIAL_ORDER_STATE);
                break;
            default:
                return;
        }
    };

    const handleDispatchInvoiceModal = (dataToInvoice, type) => {
        const isPurchasedOffline = type && type === 'offline';

        dispatch({
            type: APP_STATE_TYPES.TOGGLE_MODAL,
            kind: MODAL_KIND.REQUEST_INVOICE,
            open: true,
            props: {
                context: type,
                isLoggedIn: userIsLogged,
                order: !isPurchasedOffline ? dataToInvoice : null,
                orderNumber: !isPurchasedOffline ? dataToInvoice?.publicOrderId : null,
                ticketInfo: isPurchasedOffline ? dataToInvoice : null,
                resetValues: handleResetValues,
            },
        });
    };

    const handleDispatchLogginModal = (invoiceData, context) =>
        dispatch({
            type: APP_STATE_TYPES.TOGGLE_MODAL,
            kind: MODAL_KIND.LOGIN,
            open: true,
            props: {
                logginBeforeInvoice: true,
                redirectToLogin: () =>
                    dispatch({
                        type: APP_STATE_TYPES.TOGGLE_MODAL,
                        kind: MODAL_KIND.LOGIN,
                        open: true,
                    }),
                handleInvoiceAsGuest: () => handleDispatchInvoiceModal(invoiceData, context),
            },
        });

    const handleConsultTicket = (e) => {
        if (!ticketValues)
            return setTicketResult({ message: 'Los campos son invalidos o se encuentran vacíos' });

        const { posId, storeId, ticketId, ticketDate } = ticketValues;
        let formatStoreId = Number(storeId);
        const formatTicketDate = parseISO(ticketDate);
        const formatedDateToISO = format(formatTicketDate, 'yyyy-MM-dd');

        const invoiceData = {
            storeId,
            ticketId,
            posId,
            dateSale: formatedDateToISO,
        };

        e.preventDefault();

        if (ticketResult && ticketResult.ticketFound && ticketResult.canRequest) {
            if (!userIsLogged) {
                handleDispatchLogginModal(invoiceData, 'offline');
            } else {
                handleDispatchInvoiceModal(invoiceData, 'offline');
            }
        } else {
            getTicket({
                variables: {
                    posId,
                    storeId: String(formatStoreId),
                    ticketId,
                    ticketDate: formatedDateToISO,
                },
            }).then((res) =>
                res && res.data
                    ? setTicketResult({
                          ...get_(res, ['data', 't3bTicket']),
                          isInitialState: false,
                      })
                    : () => {
                          setTicketResult({ error: ticketError, ticketFound: false });
                          trackError({
                              label: 'Error getting information',
                              extras: {
                                  variables: ticket,
                                  error: ticketError.toString(),
                              },
                          });
                      }
            );
        }
    };

    const handleConsultOrder = (e) => {
        if (!orderValues)
            return setOrderResult({ message: 'Los campos son invalidos o se encuentran vacíos' });

        const { publicId, total } = orderValues;

        e.preventDefault();

        const invoiceData = {
            ...orderValues,
            publicOrderId: publicId,
        };

        if (orderResult && orderResult.found && orderResult.canRequest) {
            if (!userIsLogged) {
                handleDispatchLogginModal(invoiceData, 'online');
            } else {
                handleDispatchInvoiceModal(invoiceData, 'online');
            }
        } else {
            getOrder({
                variables: {
                    publicId,
                    total: Number(total),
                },
            }).then((res) =>
                res && res.data
                    ? setOrderResult(res?.data?.orderForInvoice)
                    : () => {
                          setOrderResult({ error: orderError, found: false });
                          trackError({
                              label: 'Error getting information',
                              extras: {
                                  variables: order,
                                  error: orderError.toString(),
                              },
                          });
                      }
            );
        }
    };

    return (
        <InvoiceRequest
            isDesktop={!isMobile}
            isLoading={loadingTicket || loadingOrder}
            orderStatus={orderResult}
            orderValues={orderValues}
            setOrderValues={setOrderValues}
            ticketValues={ticketValues}
            setTicketValues={setTicketValues}
            ticketStatus={ticketResult}
            handleConsultTicket={handleConsultTicket}
            handleConsultOrder={handleConsultOrder}
            handleResetValues={handleResetValues}
        />
    );
};

export default InvoicePage;
